import { CopyOutlined, FormOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Table, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import {
  type IntakeStatus,
  type TIntakeRequest,
  createIntakeRequest,
  listIntakeRequests,
  revokeIntakeRequest,
} from "../../service/intake.service";

const STATUS_TAG: Record<IntakeStatus, { color: string; label: string }> = {
  pending: { color: "blue", label: "Awaiting patient" },
  submitted: { color: "green", label: "Submitted" },
  expired: { color: "default", label: "Expired" },
  revoked: { color: "default", label: "Revoked" },
};

/**
 * "Intake form" on the patient's page: generates a link to the pain intake
 * questionnaire for this patient and lists earlier links. Submitting the form
 * creates a note on this patient, so the list refreshes the notes when one lands.
 */
export default function IntakeLinkButton({ clientId, onSubmitted }: { clientId: number; onSubmitted?: () => void }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [requests, setRequests] = useState<TIntakeRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listIntakeRequests(clientId);
      setRequests(r.data);
    } catch {
      message.error("Couldn't load intake links.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open) {
      setNewUrl(null);
      void refresh();
    }
  }, [open, refresh]);

  // While a link is pending, poll so the clinician sees the submission land.
  useEffect(() => {
    if (!open || !requests.some((r) => r.status === "pending")) return;
    const id = window.setInterval(async () => {
      const before = requests.filter((r) => r.status === "submitted").length;
      const r = await listIntakeRequests(clientId).catch(() => null);
      if (!r) return;
      setRequests(r.data);
      if (r.data.filter((x) => x.status === "submitted").length > before) onSubmitted?.();
    }, 5000);
    return () => window.clearInterval(id);
  }, [open, requests, clientId, onSubmitted]);

  const generate = async () => {
    setCreating(true);
    try {
      const r = await createIntakeRequest(clientId);
      setNewUrl(r.data.url ?? null);
      await refresh();
    } catch {
      message.error("Couldn't generate an intake link.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("Link copied");
    } catch {
      message.error("Couldn't copy; select the link and copy it manually.");
    }
  };

  const revoke = async (id: number) => {
    try {
      await revokeIntakeRequest(id);
      await refresh();
    } catch {
      message.error("Couldn't revoke that link.");
    }
  };

  return (
    <>
      <Button type="default" icon={<FormOutlined />} onClick={() => setOpen(true)}>
        Intake form
      </Button>
      <Modal
        title="Patient intake form"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Generate a link to the pain intake questionnaire for this patient. When they submit it, a note is created on
            this patient from their answers.
          </Typography.Paragraph>
          <Button type="primary" loading={creating} onClick={generate}>
            Generate intake link
          </Button>
          {newUrl && (
            <div style={{ background: "#f3f4f6", borderRadius: 6, padding: "10px 12px" }}>
              <Typography.Text strong>New link (shown once):</Typography.Text>
              <Space style={{ display: "flex", marginTop: 6 }} align="start">
                <Typography.Text code style={{ wordBreak: "break-all" }}>
                  {newUrl}
                </Typography.Text>
                <Button size="small" icon={<CopyOutlined />} onClick={() => copy(newUrl)}>
                  Copy
                </Button>
              </Space>
              <div style={{ marginTop: 6 }}>
                <Typography.Text type="secondary">
                  Valid for 72 hours. Anyone with the link can fill in the form, so share it only with the patient.
                </Typography.Text>
              </div>
            </div>
          )}
          <Table<TIntakeRequest>
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={requests}
            pagination={false}
            locale={{ emptyText: "No intake links yet" }}
            columns={[
              {
                title: "Created",
                dataIndex: "created_at",
                render: (v: string) => dayjs(v).format("MMM D, YYYY h:mm A"),
              },
              {
                title: "Status",
                dataIndex: "status",
                render: (s: IntakeStatus) => <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].label}</Tag>,
              },
              {
                title: "Expires",
                dataIndex: "expires_at",
                render: (v: string, row) => (row.status === "pending" ? dayjs(v).format("MMM D, h:mm A") : "—"),
              },
              {
                title: "",
                key: "actions",
                render: (_: unknown, row) =>
                  row.status === "pending" ? (
                    <Button size="small" danger type="link" onClick={() => revoke(row.id)}>
                      Revoke
                    </Button>
                  ) : row.status === "submitted" && row.audio_file_id ? (
                    <Typography.Text type="secondary">Note #{row.audio_file_id}</Typography.Text>
                  ) : null,
              },
            ]}
          />
        </Space>
      </Modal>
    </>
  );
}
